import type { CSSProperties } from 'react'
import type { Cell, ColumnSizingState, Header, RowData } from '@tanstack/react-table'

import {
  readColumnAlign,
  readColumnMeta,
  readGrowColumnMinWidth,
  readGrowFactor,
} from './column-meta'
import type { DataTableSurface } from './data-table.styles'
import { getPinnedCellProps, type PinnedOffsetMap } from './pinning.styles'
import type { TableCellProps } from './table-parts'

/**
 * The width (px) a header cell should render at — `header.getSize()`, the SAME
 * clamped read the body cell takes via `cell.column.getSize()` (both apply the
 * column's `min`/`max`), so the header and body planes never disagree at the size
 * bounds. Reading the raw `columnSizing[id]` instead would drift them: TanStack
 * stores the in-flight resize value clamped only to `>= 0`, not to `min`/`max`.
 *
 * `columnSizing` is the reactive `ColumnSizingState` VALUE from `useDataTable`
 * (`columnSizingState`) — the REACTIVITY KEY, not a value input. `getSize()` reads
 * the current size off the referentially-stable `header`, a read the React Compiler
 * cannot see change when only the sizing state updates — so a header cell memoized
 * on the stable `header` freezes its width mid-resize while the body cells (widths
 * computed in the preset's own render) track live. Threading `columnSizing` through
 * this call makes the compiler key it on the sizing state and recompute the width
 * every resize tick. Same RATIONALE as `resolveSortDirection` — but here the state arg
 * is a pure reactivity key, not a value input (`resolveSortDirection` reads its `sorting`
 * arg; this ignores `columnSizing` and derives the value from `header.getSize()`).
 */
export const resolveColumnWidth = <TData extends RowData>(
  header: Header<TData, unknown>,
  // The reactive `ColumnSizingState`; unused for the return VALUE, present so the
  // React Compiler keys this call on it (see doc above). `_`-prefixed per the
  // `no-unused-vars` `argsIgnorePattern`.
  _columnSizing: ColumnSizingState,
): number => header.getSize()

/** The flex/width inputs one column's track is laid out from, in either plane. */
export type ColumnTrack = {
  /** `meta.grow`: the column absorbs spare width and may shrink within the scrollport. */
  grow: boolean
  /** `flex-grow` share for a grow column. */
  growFactor: number
  /** CSS floor for a grow column. */
  growMinWidth: number
  /** The column's resolved width (px) — the flex BASIS for a grow column. */
  width: number
}

/**
 * The inline track ONE column occupies. A `meta.grow` column fills spare width but can
 * shrink within the scrollport (`flex: factor 1 width`, floored by `minWidth`); every
 * other column is a fixed `width`. Header, body and summary band all lay out through
 * THIS function, which is what keeps their column tracks aligned — a second copy of the
 * branch is how the planes drift apart at a size bound.
 */
export const getColumnTrackStyle = ({
  grow,
  growFactor,
  growMinWidth,
  width,
}: ColumnTrack): CSSProperties =>
  grow ? { flex: `${growFactor} 1 ${width}px`, minWidth: growMinWidth } : { width }

/**
 * Everything a rendered CELL needs to occupy its column's track: the alignment, the
 * sticky-pin payload, and the flex/width style. Body rows and the summary band derive
 * it identically — they render the same visible leaf columns of the same table — so
 * they share this one derivation; `DataTableHeaderCell` takes its reactive width as a
 * prop (see `resolveColumnWidth`) and composes `getColumnTrackStyle` directly.
 *
 * `surface` is the plane's elevation, which the pinned cell paints opaque
 * (`pinnedCellSurfaceClass`): the card's for a body cell, and for a summary cell the SUMMARY
 * band's own — the two chrome bands no longer always match, since `rowDivider="ruled"` blends
 * the header into the card and leaves the band lifted.
 */
export const getCellTrackProps = <TData extends RowData, TValue>(
  cell: Cell<TData, TValue>,
  pinnedOffsets: PinnedOffsetMap,
  surface: DataTableSurface,
): Pick<TableCellProps, 'align' | 'pinned' | 'style'> => ({
  align: readColumnAlign(cell.column),
  pinned: getPinnedCellProps(pinnedOffsets, cell.column.id, { variant: 'body', surface }),
  style: getColumnTrackStyle({
    grow: readColumnMeta(cell.column)?.grow === true,
    growFactor: readGrowFactor(cell.column),
    growMinWidth: readGrowColumnMinWidth(cell.column),
    width: cell.column.getSize(),
  }),
})
